import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import OBSOverlay from "./OBSOverlay";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Copy, ExternalLink, Swords, Target, Skull, EyeOff } from "lucide-react";
import { toast } from "sonner";

const OVERLAY_URLS = [
  { path: "/overlay", label: "Overlay completo (ruote + obiettivi + sub)" },
  { path: "/overlay/wheel", label: "Solo ruote" },
  { path: "/overlay/objective", label: "Solo obiettivi" },
];

export default function OBSEditor() {
  const [settings, setSettings] = useState(null);
  const origin = window.location.origin;

  const load = useCallback(async () => setSettings(await api.getSettings()), []);
  useEffect(() => { load(); }, [load]);

  const update = async (section, patch) => {
    setSettings((s) => ({ ...s, [section]: { ...s[section], ...patch } }));
    await api.saveSettings({ [section]: patch });
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("URL copiato negli appunti"); };

  if (!settings) return null;
  const o = settings.overlay;

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Overlay OBS" subtitle="Configura l'overlay, copia l'URL per la Browser Source e prova in anteprima." accent="amber" />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* URLs + guide */}
        <div className="space-y-4">
          <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
            <h3 className="font-display font-bold uppercase tracking-wide text-white mb-3">URL Browser Source</h3>
            {OVERLAY_URLS.map((u) => (
              <div key={u.path} className="mb-3">
                <div className="text-xs text-slate-400 mb-1">{u.label}</div>
                <div className="flex gap-2">
                  <Input readOnly value={`${origin}${u.path}`} className="bg-[#0b0e16] border-[#334155] text-amber-300 font-mono text-sm" data-testid={`overlay-url-${u.path.replace(/\//g, "-")}`} />
                  <Button size="icon" onClick={() => copy(`${origin}${u.path}`)} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f] shrink-0" data-testid="obs-url-copy-button"><Copy className="w-4 h-4" /></Button>
                  <a href={`${origin}${u.path}`} target="_blank" rel="noreferrer"><Button size="icon" variant="outline" className="border-slate-600 text-slate-200 shrink-0"><ExternalLink className="w-4 h-4" /></Button></a>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
            <h3 className="font-display font-bold uppercase tracking-wide text-white mb-3">Guida OBS</h3>
            <ol className="text-slate-300 text-sm space-y-1.5 list-decimal list-inside">
              <li>Apri OBS Studio</li>
              <li>Aggiungi una nuova <span className="text-amber-400 font-semibold">Browser Source</span></li>
              <li>Incolla l'URL Overlay qui sopra</li>
              <li>Imposta larghezza <span className="text-amber-400">1920</span> e altezza <span className="text-amber-400">1080</span></li>
              <li>Attiva "Controlla l'audio tramite OBS" se vuoi i suoni</li>
              <li>Premi un pulsante Test qui sotto per verificare</li>
            </ol>
          </div>

          <div className="bg-[#131722] border border-[#334155] rounded-xl p-5 space-y-5">
            <h3 className="font-display font-bold uppercase tracking-wide text-white">Personalizzazione</h3>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Titolo overlay</label>
              <Input defaultValue={o.title} onBlur={(e) => update("overlay", { title: e.target.value })} className="bg-[#0b0e16] border-[#334155] text-white mt-1" data-testid="overlay-title-input" />
            </div>
            <SliderRow label={`Dimensione ruota: ${o.wheelSize}px`} value={o.wheelSize} min={280} max={640} step={10} onChange={(v) => update("overlay", { wheelSize: v })} testid="overlay-wheelsize" />
            <SliderRow label={`Dimensione risultato: ${o.fontSize}px`} value={o.fontSize} min={28} max={96} step={2} onChange={(v) => update("overlay", { fontSize: v })} testid="overlay-fontsize" />
            <SliderRow label={`Durata animazione: ${settings.general.animationDuration}s`} value={settings.general.animationDuration} min={2} max={10} step={1} onChange={(v) => update("general", { animationDuration: v })} testid="overlay-animduration" />
            <SliderRow label={`Durata risultato: ${settings.general.resultDuration}s`} value={settings.general.resultDuration} min={2} max={15} step={1} onChange={(v) => update("general", { resultDuration: v })} testid="overlay-resultduration" />
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Posizione</label>
              <Select value={o.position} onValueChange={(v) => update("overlay", { position: v })}>
                <SelectTrigger className="bg-[#0b0e16] border-[#334155] text-white mt-1" data-testid="overlay-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["center", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Suoni overlay</span>
              <Switch checked={settings.audio.enabled} onCheckedChange={(v) => update("audio", { enabled: v })} data-testid="overlay-audio-toggle" />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="bg-[#131722] border border-[#334155] rounded-xl p-5 sticky top-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold uppercase tracking-wide text-white">Anteprima Overlay</h3>
              <span className="text-xs text-slate-500 font-mono">1920×1080</span>
            </div>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-[#334155]"
              style={{
                aspectRatio: "16 / 9",
                backgroundImage: "linear-gradient(45deg,#1a1d27 25%,transparent 25%),linear-gradient(-45deg,#1a1d27 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#1a1d27 75%),linear-gradient(-45deg,transparent 75%,#1a1d27 75%)",
                backgroundSize: "24px 24px",
                backgroundPosition: "0 0,0 12px,12px -12px,-12px 0",
                backgroundColor: "#0f1117",
              }}
              data-testid="overlay-preview"
            >
              <div style={{ transform: "scale(0.42)", transformOrigin: "top left", width: "238%", height: "238%" }}>
                <OBSOverlay filterMode="all" preview />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button onClick={() => api.spin("attack")} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="preview-test-attack"><Swords className="w-4 h-4 mr-2" /> Attacco</Button>
              <Button onClick={() => api.spin("penalty")} className="bg-red-600 hover:bg-red-500 text-white" data-testid="preview-test-penalty"><Skull className="w-4 h-4 mr-2" /> Penalità</Button>
              <Button onClick={() => api.drawObjective("medium")} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="preview-test-objective"><Target className="w-4 h-4 mr-2" /> Obiettivo</Button>
              <Button onClick={() => api.overlayHide()} variant="outline" className="border-slate-600 text-slate-200" data-testid="preview-hide"><EyeOff className="w-4 h-4 mr-2" /> Nascondi</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} className="mt-3" data-testid={testid} />
    </div>
  );
}
